"use client";

import { useState } from "react";
import { FiCalendar, FiClipboard, FiCopy, FiEdit2, FiPrinter, FiTrash2 } from "react-icons/fi";
import { LuCheck, LuClock, LuCopy, LuSave } from "react-icons/lu";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { useProgressNavigation } from "@/components/progress-bar";
import { NotePrintDialogHost } from "@/components/scribe/note-print-dialog-host";
import Button from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger, DropdownMenuTrigger, DropdownSettingsButton } from "@/components/ui/dropdown";
import Input from "@/components/ui/input";
import { useUser } from "@/contexts/user-provider";
import { useDuplicateNote } from "@/hooks/use-duplicate-note";
import { useNotePrintFlow } from "@/hooks/use-note-print-flow";
import { isLocalNoteId } from "@/hooks/use-selected-note";
import { formatNoteAnswersToHtml, formatNoteAnswersToText, stripHtmlToText } from "@/lib/note-utils";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { NoteDetail, NoteListItem } from "@/types/notes";
import { useDocumentTemplatesQuery, useGenerateDocumentMutation } from "@/api/provider/generated-document/generated-document-query";
import { useArchiveNoteMutation, useUpdateNoteTitleMutation } from "@/api/provider/scribe/note-mutation";

/** Makes the Print row print something other than the note. Omit it and the row
 *  behaves exactly as before. */
export interface NoteActionsPrintOverride {
  label: string;
  /** Absent while that document isn't printable yet: row disabled, "Preparing…". */
  onPrint?: () => void;
  isPreparing?: boolean;
}
interface NoteActionsBarProps {
  noteItem: NoteListItem | NoteDetail;
  // Save functionality (optional)
  onSave?: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
  // Unsaved answer edits, keyed the same way as NoteAnswersSection's
  // localAnswerChanges. When provided, Copy and Print reflect the same
  // conditional visibility as the on-screen editor instead of just the last
  // saved answers — otherwise a gated question that only appears once the
  // provider saves can be missing (or, for the reverse edit, wrongly present)
  // from a print/copy taken mid-edit. Only pass this from a call site that
  // actually has draft state; omitting it keeps today's saved-only behavior.
  localAnswerChanges?: Record<string, Record<string, any>>;
  // Copy/print types
  type?: "note" | "transcript";
  printOverride?: NoteActionsPrintOverride;
  // Additional actions rendered in dropdown menu
  dropdownActions?: React.ReactNode;
  // Revisions submenu content (rendered inside a submenu)
  revisionsContent?: React.ReactNode;
  // Additional actions rendered inline (legacy, for sign-off button)
  additionalActions?: React.ReactNode;
  // Shows a Delete row; called once the note is deleted so the caller can leave
  // a screen that would otherwise keep showing it. Omit it to hide the row.
  onDeleted?: () => void;
  className?: string;
}
export default function NoteActionsBar({
  noteItem,
  onSave,
  isSaving = false,
  hasUnsavedChanges = false,
  localAnswerChanges,
  type = "note",
  printOverride,
  dropdownActions,
  revisionsContent,
  additionalActions,
  onDeleted,
  className = ""
}: NoteActionsBarProps) {
  const {
    toast
  } = useToast();
  const {
    currentUser
  } = useUser();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const [copyStatus, setCopyStatus] = useState<{
    [key: string]: boolean;
  }>({});
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const duplicateNoteMutation = useDuplicateNote();
  const updateTitleMutation = useUpdateNoteTitleMutation();
  const archiveMutation = useArchiveNoteMutation();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const canRenameNote = type === "note" && Boolean(noteItem.id) && !isLocalNoteId(noteItem.id) && hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  const canDeleteNote = type === "note" && Boolean(onDeleted) && Boolean(noteItem.id) && !isLocalNoteId(noteItem.id) && hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_ARCHIVE);
  const openRename = () => {
    setRenameValue(noteItem.title ?? "");
    setIsRenameOpen(true);
  };
  const handleRenameSave = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== noteItem.title) {
      updateTitleMutation.mutate({
        noteId: noteItem.id,
        title: trimmed
      });
    }
    setIsRenameOpen(false);
  };
  const generateDocumentMutation = useGenerateDocumentMutation();
  const printFlow = useNotePrintFlow();
  const noteHasAnswers = "hasAnswers" in noteItem ? Boolean(noteItem.hasAnswers) : Boolean((noteItem as NoteDetail).answers?.length);

  // Only offered once the note is persisted (no unsaved changes, real id) and has answers.
  const canGenerateDocument = type === "note" && !hasUnsavedChanges && Boolean(noteItem.id) && !isLocalNoteId(noteItem.id) && noteHasAnswers && hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);

  // Only fetch templates when treatment-plan generation is actually available (the endpoint
  // requires notes-write, so an unconditional fetch would 401/403 on unrelated renders).
  const {
    data: documentTemplates
  } = useDocumentTemplatesQuery(canGenerateDocument);
  const handleGenerateDocument = (templateId?: string) => {
    generateDocumentMutation.mutate({
      noteId: noteItem.id,
      templateId
    }, {
      onSuccess: plan => {
        if (plan) navigateWithProgress(`/treatment-plans/${plan.id}`);
      },
      onError: async error => {
        toast({
          title: "Couldn't create treatment plan",
          description: await parseError(error),
          variant: "error"
        });
      }
    });
  };

  // Format answers into text content if available, otherwise use summary
  const content = (() => {
    const noteDetail = noteItem as NoteDetail;
    // NoteDetail has answers, NoteListItem doesn't
    if ("answers" in noteDetail && noteDetail.answers && noteDetail.answers.length > 0 && noteDetail.template) {
      // Plain text: this feeds the copy fallback and a "is there anything to
      // copy" check, so neither wants markup.
      return formatNoteAnswersToText(noteDetail, undefined, {
        plainText: true
      }, localAnswerChanges);
    }
    // For NoteListItem, use summary; for NoteDetail without answers, also use summary
    return noteItem.summary ? stripHtmlToText(noteItem.summary) : undefined;
  })();
  const handleCopy = async (contentToCopy: string, copyType: string, htmlToCopy?: string) => {
    try {
      // Write an HTML flavor alongside plain text so pasting into a rich-text
      // editor (email template, treatment plan) keeps the note's paragraphs,
      // headings and lists instead of collapsing into one run-on paragraph.
      if (htmlToCopy && typeof ClipboardItem !== "undefined") {
        await navigator.clipboard.write([new ClipboardItem({
          "text/html": new Blob([htmlToCopy], {
            type: "text/html"
          }),
          "text/plain": new Blob([contentToCopy], {
            type: "text/plain"
          })
        })]);
      } else {
        await navigator.clipboard.writeText(contentToCopy);
      }
      // No success toast: the button swaps to a check for the same couple of
      // seconds, and a toast would land on the Save and Sign off controls beside
      // it.
      const statusKey = noteItem.id ? `${noteItem.id}-${copyType}` : copyType;
      setCopyStatus(prev => ({
        ...prev,
        [statusKey]: true
      }));
      setTimeout(() => {
        setCopyStatus(prev_0 => ({
          ...prev_0,
          [statusKey]: false
        }));
      }, 2000);
    } catch (error_0) {
      const errorMessage = await parseError(error_0);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "error"
      });
    }
  };
  const copyStatusKey = noteItem.id ? `${noteItem.id}-${type}` : type;
  // Deliberately not keyed off `content`: that text is condition- and
  // draft-filtered, so a mid-edit answer that gates the last visible question
  // empties it and would take the Print row away — along with the demographics,
  // medical codes, medications and photos that print regardless of the answers.
  // `noteHasAnswers` reads the right field for either shape (NoteListItem
  // carries hasAnswers, NoteDetail only the answers themselves).
  const canPrintNote = noteHasAnswers || Boolean(noteItem.summary) || Boolean(content);
  const hasDropdownActions = type === "note" || dropdownActions || revisionsContent;
  const printLabel = printOverride?.label ?? "Print / Export to PDF";
  const isPreparingPrint = printOverride ? Boolean(printOverride.isPreparing) : printFlow.state.isPreparing;
  const printAction = printOverride ? printOverride.onPrint : () => void printFlow.actions.requestPrint(noteItem, localAnswerChanges);
  // An override brings its own document, so `canPrintNote` doesn't apply to it.
  const showPrint = printOverride ? true : canPrintNote;
  return <div className={cx("flex items-center gap-1", className)}>
      <NotePrintDialogHost printFlow={printFlow} />
      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename note</DialogTitle>
          </DialogHeader>
          <Input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)} onKeyDown={e_0 => {
          if (e_0.key === "Enter") {
            e_0.preventDefault();
            handleRenameSave();
          }
        }} placeholder="Note title" />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsRenameOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleRenameSave} isLoading={updateTitleMutation.isPending} disabled={!renameValue.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {canDeleteNote && <DeleteConfirmationModal title="Delete Note" subtitle="Are you sure you want to delete this note? This action cannot be undone." isOpen={isDeleteOpen} setIsOpen={setIsDeleteOpen} onConfirm={() => {
      // Any version's id deletes the whole note, revisions included.
      archiveMutation.mutate({
        noteId: noteItem.id
      }, {
        onSuccess: () => onDeleted?.()
      });
    }} />}
      {additionalActions}
      {onSave && <Button variant="primary" className="p-1.5 text-caption backdrop-blur-sm" onClick={onSave} isLoading={isSaving} loadingText="Saving..." icon={<LuSave className="size-3" />} disabled={!hasUnsavedChanges} tooltip={!hasUnsavedChanges ? "No changes to save" : undefined}>
          Save
        </Button>}
      {content && <Button variant="secondary" className="h-8 bg-background/80 p-1.5 backdrop-blur-sm hover:bg-hover" onClick={() => {
      const noteDetail_0 = noteItem as NoteDetail;
      const hasStructuredAnswers = "answers" in noteDetail_0 && noteDetail_0.answers && noteDetail_0.answers.length > 0 && noteDetail_0.template;
      const clipboardContent = hasStructuredAnswers ? formatNoteAnswersToText(noteDetail_0, undefined, {
        plainSectionHeadings: true,
        dedupeSectionLabels: true,
        plainText: true
      }, localAnswerChanges) : noteItem.summary ? stripHtmlToText(noteItem.summary) : undefined;
      const clipboardHtml = hasStructuredAnswers ? formatNoteAnswersToHtml(noteDetail_0, undefined, {
        dedupeSectionLabels: true
      }, localAnswerChanges) : noteItem.summary || undefined;
      void handleCopy(clipboardContent ?? content, type, clipboardHtml);
    }} tooltip={`Copy ${type} to clipboard`}>
          {copyStatus[copyStatusKey] ? <LuCheck className="size-3 text-status-success-text" /> : <LuCopy className="size-3 text-foreground-secondary" />}
          <p className="sr-only">Copy {type}</p>
        </Button>}
      {hasDropdownActions && <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <DropdownSettingsButton variant="secondary" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="text-right">
            {type === "note" && <>
                {showPrint && <DropdownMenuItem disabled={isPreparingPrint || !printAction} onSelect={e_1 => {
            e_1.preventDefault();
            printAction?.();
          }}>
                    <FiPrinter className="mr-2 size-4" />
                    {isPreparingPrint ? <span>{"Preparing..."}</span> : <span>{printLabel}</span>}
                  </DropdownMenuItem>}
                {noteItem.eventId && <DropdownMenuItem onSelect={e_2 => {
            e_2.preventDefault();
            navigateWithProgress(`/check-in/calendar/?eventId=${noteItem.eventId}`);
          }}>
                    <FiCalendar className="mr-2 size-4" />
                    <span>Go to linked appointment</span>
                  </DropdownMenuItem>}
                <DropdownMenuItem disabled={duplicateNoteMutation.isPending} onSelect={e_3 => {
            e_3.preventDefault();
            duplicateNoteMutation.mutate(noteItem.id);
          }}>
                  <FiCopy className="mr-2 size-4" />
                  {duplicateNoteMutation.isPending ? <span>{"Duplicating..."}</span> : <span>{"Duplicate"}</span>}
                </DropdownMenuItem>
                {canRenameNote && <DropdownMenuItem onSelect={e_4 => {
            e_4.preventDefault();
            openRename();
          }}>
                    <FiEdit2 className="mr-2 size-4" />
                    <span>Rename note</span>
                  </DropdownMenuItem>}
                {canGenerateDocument && <>
                    <DropdownMenuItem disabled={generateDocumentMutation.isPending} onSelect={e_5 => {
              e_5.preventDefault();
              handleGenerateDocument();
            }}>
                      <FiClipboard className="mr-2 size-4" />
                      {generateDocumentMutation.isPending ? <span>{"Generating..."}</span> : <span>{"Generate treatment plan"}</span>}
                    </DropdownMenuItem>
                    {documentTemplates && documentTemplates.length > 0 && <DropdownMenuSubMenu>
                        <DropdownMenuSubMenuTrigger arrowPosition="left">
                          <FiClipboard className="mr-2 size-4" />
                          From template
                        </DropdownMenuSubMenuTrigger>
                        <DropdownMenuSubMenuContent className="max-h-80 overflow-y-auto">
                          {documentTemplates.map(tpl => <DropdownMenuItem key={tpl.id} onSelect={e_6 => {
                  e_6.preventDefault();
                  handleGenerateDocument(tpl.id);
                }}>
                              {tpl.name}
                            </DropdownMenuItem>)}
                        </DropdownMenuSubMenuContent>
                      </DropdownMenuSubMenu>}
                  </>}
              </>}
            {revisionsContent && <DropdownMenuSubMenu>
                <DropdownMenuSubMenuTrigger arrowPosition="left">
                  <LuClock className="mr-2 size-4" />
                  View Revisions
                </DropdownMenuSubMenuTrigger>
                <DropdownMenuSubMenuContent className="w-72">
                  {revisionsContent}
                </DropdownMenuSubMenuContent>
              </DropdownMenuSubMenu>}
            {dropdownActions}
            {canDeleteNote && <>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={archiveMutation.isPending} onSelect={e_7 => {
            e_7.preventDefault();
            setIsDeleteOpen(true);
          }} className="text-status-error-text" icon={<FiTrash2 className="h-4 w-4 text-status-error-text" />}>
                  Delete
                </DropdownMenuItem>
              </>}
          </DropdownMenuContent>
        </DropdownMenu>}
    </div>;
}